import React, { createContext, useState, useContext, useEffect } from 'react';
import {
   collection,
   query,
   where,
   getDocs,
   getDoc,
   doc,
   updateDoc,
   deleteDoc,
   orderBy,
   serverTimestamp,
} from 'firebase/firestore';
import { db, storage } from '../firebase/config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useUser } from './UserContext';
import { useSubscription } from './SubscriptionContext';
import { Event, EventFormData } from '../types/Event';
import { get_all_events_and_shared_events } from '../services';

interface EventContextType {
   events: Event[];
   userEvents: Event[];
   loading: boolean;
   error: string | null;
   createEvent: (eventData: EventFormData) => Promise<string>;
   getEvent: (eventId: string) => Promise<Event | null>;
   updateEvent: (eventId: string, eventData: Partial<Event>) => Promise<void>;
   deleteEvent: (eventId: string) => Promise<void>;
   uploadEventCoverImage: (eventId: string, file: File) => Promise<string>;
   refreshEvents: () => Promise<void>;
}

const EventContext = createContext<EventContextType | undefined>(undefined);

export const useEvents = () => {
   const context = useContext(EventContext);
   if (!context) {
      throw new Error('useEvents must be used within an EventProvider');
   }
   return context;
};

export const EventProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
   const [events, setEvents] = useState<Event[]>([]);
   const [userEvents, setUserEvents] = useState<Event[]>([]);
   const [loading, setLoading] = useState<boolean>(true);
   const [error, setError] = useState<string | null>(null);
   const { currentUser } = useUser();

   useEffect(() => {
      if (currentUser) {
         fetchEvents();
      } else {
         setEvents([]);
         setUserEvents([]);
         setLoading(false);
      }
   }, [currentUser]);

   const fetchEvents = async () => {
      if (!currentUser) return;

      setLoading(true);
      setError(null);

      try {
         //  // First, get events the user created - check both userId and createdBy fields
         //  let userEventsData: Event[] = [];

         //  // Query with userId field
         //  const userEventsQuery1 = query(
         //     collection(db, 'events'),
         //     where('userId', '==', currentUser.uid)
         //  );

         //  // Query with createdBy field
         //  const userEventsQuery2 = query(
         //     collection(db, 'events'),
         //     where('createdBy', '==', currentUser.uid)
         //  );

         //  // Get public events
         //  const publicEventsQuery = query(collection(db, 'events'), where('isPublic', '==', true));

         //  // Get events where user is a participant via eventParticipants collection
         //  const participantsQuery = query(
         //     collection(db, 'eventParticipants'),
         //     where('userId', '==', currentUser.uid),
         //     where('status', '==', 'accepted')
         //  );

         //  const [
         //     userEventsSnapshot1,
         //     userEventsSnapshot2,
         //     publicEventsSnapshot,
         //     participantsSnapshot,
         //  ] = await Promise.all([
         //     getDocs(userEventsQuery1),
         //     getDocs(userEventsQuery2),
         //     getDocs(publicEventsQuery),
         //     getDocs(participantsQuery),
         //  ]);

         //  // Combine and deduplicate events
         //  const allEvents: Event[] = [];
         //  const eventIds = new Set<string>();

         //  // Add events the user created with userId field
         //  userEventsSnapshot1.forEach((doc) => {
         //     const eventData = doc.data();
         //     const event = {
         //        id: doc.id,
         //        title: eventData.title,
         //        description: eventData.description,
         //        eventType: eventData.eventType,
         //        startDate: eventData.startDate,
         //        endDate: eventData.endDate,
         //        createdAt: eventData.createdAt?.toDate() || new Date(),
         //        updatedAt: eventData.updatedAt?.toDate() || undefined,
         //        location: eventData.location || null,
         //        coverImageUrl: eventData.coverImageUrl || null,
         //        isPublic: eventData.isPublic || false,
         //        userId: eventData.userId,
         //     } as Event;

         //     allEvents.push(event);
         //     userEventsData.push(event);
         //     eventIds.add(doc.id);
         //  });

         //  // Add events the user created with createdBy field
         //  userEventsSnapshot2.forEach((doc) => {
         //     // Skip if already added from userId query
         //     if (!eventIds.has(doc.id)) {
         //        const eventData = doc.data();
         //        const event = {
         //           id: doc.id,
         //           title: eventData.title,
         //           description: eventData.description,
         //           eventType: eventData.eventType,
         //           startDate: eventData.startDate,
         //           endDate: eventData.endDate,
         //           createdAt: eventData.createdAt?.toDate() || new Date(),
         //           updatedAt: eventData.updatedAt?.toDate() || undefined,
         //           location: eventData.location || null,
         //           coverImageUrl: eventData.coverImageUrl || null,
         //           isPublic: eventData.isPublic || false,
         //           userId: eventData.userId,
         //        } as Event;

         //        allEvents.push(event);
         //        userEventsData.push(event);
         //        eventIds.add(doc.id);
         //     }
         //  });

         //  // Add public events (if not already added)
         //  publicEventsSnapshot.forEach((doc) => {
         //     if (!eventIds.has(doc.id)) {
         //        const eventData = doc.data();
         //        allEvents.push({
         //           id: doc.id,
         //           title: eventData.title,
         //           description: eventData.description,
         //           eventType: eventData.eventType,
         //           startDate: eventData.startDate,
         //           endDate: eventData.endDate,
         //           createdAt: eventData.createdAt?.toDate() || new Date(),
         //           updatedAt: eventData.updatedAt?.toDate() || undefined,
         //           location: eventData.location || null,
         //           coverImageUrl: eventData.coverImageUrl || null,
         //           isPublic: eventData.isPublic || false,
         //           userId: eventData.userId,
         //        } as Event);
         //        eventIds.add(doc.id);
         //     }
         //  });

         //  // Add events where user is a participant
         //  const eventIdsFromParticipants = new Set<string>();

         //  participantsSnapshot.forEach((doc) => {
         //     const participantData = doc.data();
         //     const eventId = participantData.eventId;

         //     // Skip events we already have
         //     if (eventIds.has(eventId)) {
         //        return;
         //     }

         //     eventIdsFromParticipants.add(eventId);
         //  });

         //  // Fetch all events from participant entries
         //  if (eventIdsFromParticipants.size > 0) {
         //     // Convert Set to Array before iterating
         //     const eventIdsArray = Array.from(eventIdsFromParticipants);

         //     for (const eventId of eventIdsArray) {
         //        try {
         //           const eventDoc = await getDoc(doc(db, 'events', eventId));

         //           if (eventDoc.exists()) {
         //              const data = eventDoc.data();

         //              // Process timestamps
         //              const startDate = data.startDate?.toDate ? data.startDate.toDate() : new Date();
         //              const endDate = data.endDate?.toDate ? data.endDate.toDate() : null;

         //              const event: Event = {
         //                 id: eventId,
         //                 title: data.title || 'Untitled Event',
         //                 description: data.description || '',
         //                 eventType: data.eventType || 'social',
         //                 startDate,
         //                 endDate,
         //                 createdAt: data.createdAt?.toDate() || new Date(),
         //                 updatedAt: data.updatedAt?.toDate() || undefined,
         //                 location: data.location || null,
         //                 coverImageUrl: data.coverImageUrl || null,
         //                 isPublic: data.isPublic || false,
         //                 userId: data.userId,
         //              };

         //              allEvents.push(event);
         //              eventIds.add(eventId);
         //           }
         //        } catch (err) {
         //           console.error(`Error fetching event ${eventId}:`, err);
         //        }
         //     }
         //  }

         //  // Also fetch events the user was invited to via eventInvitations
         //  try {
         //     const invitedEventsQuery = query(
         //        collection(db, 'eventInvitations'),
         //        where('email', '==', currentUser.email),
         //        where('status', '==', 'accepted')
         //     );

         //     const invitedEventsSnapshot = await getDocs(invitedEventsQuery);

         //     // For each invitation, get the actual event data
         //     for (const inviteDoc of invitedEventsSnapshot.docs) {
         //        const inviteData = inviteDoc.data();
         //        const eventId = inviteData.eventId;

         //        // Skip if we already have this event
         //        if (eventIds.has(eventId)) {
         //           continue;
         //        }

         //        // Get the event details
         //        try {
         //           const eventDoc = await getDoc(doc(db, 'events', eventId));
         //           if (eventDoc.exists()) {
         //              const data = eventDoc.data();

         //              // Process timestamps
         //              const startDate = data.startDate?.toDate ? data.startDate.toDate() : new Date();
         //              const endDate = data.endDate?.toDate ? data.endDate.toDate() : null;

         //              const event: Event = {
         //                 id: eventId,
         //                 title: data.title || 'Untitled Event',
         //                 description: data.description || '',
         //                 eventType: data.eventType || 'social',
         //                 startDate,
         //                 endDate,
         //                 createdAt: data.createdAt?.toDate() || new Date(),
         //                 updatedAt: data.updatedAt?.toDate() || undefined,
         //                 location: data.location || null,
         //                 coverImageUrl: data.coverImageUrl || null,
         //                 isPublic: data.isPublic || false,
         //                 userId: data.userId,
         //              };

         //              allEvents.push(event);
         //              eventIds.add(eventId);
         //           }
         //        } catch (eventError) {
         //           console.error(`Error fetching event ${eventId}:`, eventError);
         //        }
         //     }
         const allEvents = (await get_all_events_and_shared_events(
            currentUser.uid,
            currentUser.email || ''
         )) as any[];

         setEvents(allEvents);
      } catch (err) {
         setError('Failed to load events');
      } finally {
         setLoading(false);
      }
   };

   const createEvent = async (eventData: EventFormData): Promise<string> => {
      try {
         if (!currentUser) {
            throw new Error('You must be logged in to create an event');
         }

         // Extract the cover image file from eventData before sending to backend
         const { coverImage, ...eventDataWithoutFile } = eventData;

         // Upload cover image first if provided
         let coverImageUrl = '';
         if (coverImage) {
            // Create a temporary event ID for cover image upload
            const tempEventId = Date.now().toString();
            coverImageUrl = await uploadEventCoverImage(tempEventId, coverImage);
         }

         // Call backend API to create event
         const { getAuthHeaders } = await import('../utils/auth');
         const headers = await getAuthHeaders();
         const response = await fetch(`${process.env.REACT_APP_API_URL}/api/events`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
               ...eventDataWithoutFile,
               userId: currentUser.uid,
               startDate: eventDataWithoutFile.startDate?.toISOString
                  ? eventDataWithoutFile.startDate.toISOString()
                  : eventDataWithoutFile.startDate,
               endDate: eventDataWithoutFile.endDate?.toISOString
                  ? eventDataWithoutFile.endDate.toISOString()
                  : eventDataWithoutFile.endDate,
               coverImageUrl: coverImageUrl,
            }),
         });

         const result = await response.json();

         if (!response.ok) {
            throw new Error(result.error || 'Failed to create event');
         }

         // Refresh the events list
         await fetchEvents();

         return result.eventId;
      } catch (err) {
         console.error('Error creating event:', err);
         setError('Failed to create event. Please try again.');
         throw err;
      }
   };

   const getEvent = async (eventId: string): Promise<Event | null> => {
      try {
         const eventDoc = await getDoc(doc(db, 'events', eventId));

         if (!eventDoc.exists()) {
            return null;
         }

         return { id: eventDoc.id, ...eventDoc.data() } as Event;
      } catch (err) {
         console.error('Error getting event:', err);
         setError('Failed to load event details. Please try again.');
         return null;
      }
   };

   const updateEvent = async (eventId: string, eventData: Partial<Event>): Promise<void> => {
      try {
         await updateDoc(doc(db, 'events', eventId), {
            ...eventData,
            updatedAt: serverTimestamp(),
         });

         // Refresh the events list
         await fetchEvents();
      } catch (err) {
         console.error('Error updating event:', err);
         setError('Failed to update event. Please try again.');
         throw err;
      }
   };

   const deleteEvent = async (eventId: string): Promise<void> => {
      try {
         if (!currentUser) {
            throw new Error('User not authenticated');
         }

         const { createApiUrl } = await import('../utils/apiConfig');
         const apiUrl = createApiUrl(`/api/events/${eventId}?userId=${currentUser.uid}`);

         console.log('🗑️ Deleting event:', eventId, 'API URL:', apiUrl);

         const { getAuthHeaders } = await import('../utils/auth');
         const headers = await getAuthHeaders();

         const response = await fetch(apiUrl, {
            method: 'DELETE',
            headers,
         });

         if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to delete event');
         }

         // Refresh the events list
         await fetchEvents();
      } catch (err) {
         console.error('Error deleting event:', err);
         setError('Failed to delete event. Please try again.');
         throw err;
      }
   };

   const uploadEventCoverImage = async (eventId: string, file: File): Promise<string> => {
      try {
         const storageRef = ref(storage, `events/${eventId}/cover`);
         await uploadBytes(storageRef, file);

         const downloadURL = await getDownloadURL(storageRef);
         return downloadURL;
      } catch (err) {
         console.error('Error uploading event cover image:', err);
         throw new Error('Failed to upload cover image');
      }
   };

   const refreshEvents = async (): Promise<void> => {
      await fetchEvents();
   };

   return (
      <EventContext.Provider
         value={{
            events,
            userEvents,
            loading,
            error,
            createEvent,
            getEvent,
            updateEvent,
            deleteEvent,
            uploadEventCoverImage,
            refreshEvents,
         }}
      >
         {children}
      </EventContext.Provider>
   );
}; 